// =============================================================================
// Widget gallery — the "+ Widget" picker, modelled on the iOS widget sheet.
//
//   category  →  instance (which calendar / list / account)  →  style + size
//
// The instance step is skipped when a category has only one (or no) instance,
// so simple widgets stay two taps away. Search jumps straight to a style.
// Categories whose feature is switched off in Settings never appear.
// =============================================================================

import React, { useMemo, useState } from 'react';
import { X, ChevronRight, ArrowLeft, Search, Plus } from 'lucide-react';
import {
  availableCategories,
  resolveInstances,
  SIZE_LABELS
} from './catalog';

// Little proportional sketch of how much room a size takes on the grid.
const SizePreview = ({ size }) => {
  const cols = 12;
  const cells = Array.from({ length: cols }, (_, i) => i);
  const rows = Math.min(4, Math.max(1, Math.round(size.h / 3)));
  return (
    <div className="flex flex-col gap-0.5 w-full max-w-[180px]">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-0.5">
          {cells.map((c) => (
            <div
              key={c}
              className={`h-2 flex-1 rounded-[2px] ${
                c < size.w ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>
      ))}
    </div>
  );
};

const WidgetGallery = ({ data, t, onAdd, onClose }) => {
  const [category, setCategory] = useState(null);
  const [instance, setInstance] = useState(null);
  const [variant, setVariant] = useState(null);
  const [sizeKey, setSizeKey] = useState(null);
  const [query, setQuery] = useState('');

  const categories = useMemo(() => availableCategories(data), [data]);

  // Flat search across every visible category/variant pair.
  const searchHits = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const hits = [];
    for (const cat of categories) {
      const catLabel = t(cat.labelKey, cat.fallback);
      for (const v of cat.variants) {
        const label = t(v.labelKey, v.fallback);
        const desc = t(v.descKey, v.descFallback);
        if (
          label.toLowerCase().includes(q) ||
          catLabel.toLowerCase().includes(q) ||
          desc.toLowerCase().includes(q)
        ) {
          hits.push({ cat, variant: v, catLabel, label, desc });
        }
      }
    }
    return hits;
  }, [query, categories, t]);

  const openCategory = (cat) => {
    const instances = resolveInstances(cat, data);
    setCategory(cat);
    setQuery('');
    if (instances.length > 1) {
      setInstance(null);
    } else {
      setInstance(instances[0] || null);
    }
    setVariant(null);
    setSizeKey(null);
  };

  const openVariant = (v) => {
    setVariant(v);
    setSizeKey(v.defaultSize || Object.keys(v.sizes)[0]);
  };

  const back = () => {
    if (variant) {
      setVariant(null);
      setSizeKey(null);
      return;
    }
    const instances = category ? resolveInstances(category, data) : [];
    if (instance && instances.length > 1) {
      setInstance(null);
      return;
    }
    setCategory(null);
    setInstance(null);
  };

  const confirm = () => {
    if (!variant) return;
    const size = variant.sizes[sizeKey] || Object.values(variant.sizes)[0];
    onAdd(variant.id, instance ? instance.id : null, size);
    onClose();
  };

  const instances = category ? resolveInstances(category, data) : [];
  const instanceName = (inst) => (inst.label ? inst.label : t(inst.labelKey, inst.fallback));

  // "Kalender: Familien-Kalender" — always name the chosen instance, also
  // when there was only one and the picker skipped the instance step.
  const scopeLine =
    category && instance
      ? `${t(category.labelKey, category.fallback)}: ${instanceName(instance)}`
      : null;

  const title = variant
    ? t(variant.labelKey, variant.fallback)
    : category
    ? scopeLine || t(category.labelKey, category.fallback)
    : t('dashw.galleryTitle', 'Widget hinzufügen');

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[88vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
          {(category || variant) && (
            <button
              onClick={back}
              className="p-1.5 -ml-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
              aria-label={t('common.back', 'Zurück')}
            >
              <ArrowLeft size={18} />
            </button>
          )}
          <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex-1 truncate">{title}</h3>
          <button
            onClick={onClose}
            className="p-1.5 -mr-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500"
            aria-label={t('common.close', 'Schließen')}
          >
            <X size={18} />
          </button>
        </div>

        {/* Search — only on the top level */}
        {!category && (
          <div className="px-4 py-2 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('dashw.searchWidgets', 'In Widgets suchen')}
                className="input w-full !pl-9"
              />
            </div>
          </div>
        )}

        <div className="overflow-y-auto flex-1 px-4 py-3">
          {/* --- search results ------------------------------------------ */}
          {searchHits && (
            searchHits.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-8">
                {t('dashw.noResults', 'Nichts gefunden.')}
              </p>
            ) : (
              <div className="space-y-1.5">
                {searchHits.map(({ cat, variant: v, catLabel, label, desc }) => (
                  <button
                    key={`${cat.id}-${v.id}`}
                    onClick={() => {
                      openCategory(cat);
                      openVariant(v);
                    }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-400 text-left"
                  >
                    <cat.icon size={18} className="text-primary-600 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {label}
                      </p>
                      <p className="text-xs text-gray-500 truncate">{catLabel} · {desc}</p>
                    </div>
                    <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )
          )}

          {/* --- step 1: categories --------------------------------------- */}
          {!searchHits && !category && (
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => openCategory(cat)}
                  className="flex flex-col items-start gap-2 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-gray-50 dark:hover:bg-gray-800 text-left"
                >
                  <cat.icon size={22} className="text-primary-600" />
                  <div className="min-w-0 w-full">
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {t(cat.labelKey, cat.fallback)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {t('dashw.styleCount', '{n} Stile', { n: cat.variants.length })}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* --- step 2: instances --------------------------------------- */}
          {!searchHits && category && !instance && instances.length > 1 && (
            <div className="space-y-1.5">
              <p className="text-xs text-gray-500 mb-2">
                {t('dashw.pickInstance', 'Welche(n)?')}
              </p>
              {instances.map((inst) => (
                <button
                  key={inst.id}
                  onClick={() => setInstance(inst)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-400 text-left"
                >
                  <category.icon size={18} className="text-primary-600 flex-shrink-0" />
                  <span className="flex-1 text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                    {instanceName(inst)}
                  </span>
                  <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* --- step 3a: styles ----------------------------------------- */}
          {!searchHits && category && (instance || instances.length <= 1) && !variant && (
            <div className="space-y-1.5">
              {category.variants.map((v) => (
                <button
                  key={v.id}
                  onClick={() => openVariant(v)}
                  className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-400 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {t(v.labelKey, v.fallback)}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{t(v.descKey, v.descFallback)}</p>
                  </div>
                  <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* --- step 3b: size ------------------------------------------- */}
          {!searchHits && variant && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">{t(variant.descKey, variant.descFallback)}</p>
              {scopeLine && <p className="text-xs text-gray-400 truncate">{scopeLine}</p>}
              <div>
                <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
                  {t('dashw.pickSize', 'Größe')}
                </p>
                <div className="flex gap-2">
                  {Object.keys(variant.sizes).map((key) => (
                    <button
                      key={key}
                      onClick={() => setSizeKey(key)}
                      className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                        sizeKey === key
                          ? 'bg-primary-600 text-white border-primary-600'
                          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      {t(SIZE_LABELS[key].key, SIZE_LABELS[key].fallback)}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex justify-center py-2">
                <SizePreview size={variant.sizes[sizeKey] || Object.values(variant.sizes)[0]} />
              </div>
              <p className="text-xs text-gray-400 text-center">
                {t('dashw.resizeHint', 'Die Größe lässt sich später jederzeit ändern.')}
              </p>
              <button
                onClick={confirm}
                className="btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                <Plus size={16} />
                {t('dashw.addWidget', 'Widget hinzufügen')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WidgetGallery;
